import { gql, useQuery } from "@apollo/client";
import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import type { IMediaMetaObjectQuery } from "@repo/shopify/queries/metaobject-gql";
import { mediaMetaobjectQuery } from "@repo/shopify/queries/metaobject-gql";
import classNames from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Outlet, useLocation } from "react-router-dom";

import Button from "@atoms/Button";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";
import Toast from "@atoms/Toast";

import Modal from "@molecules/Modal";
import NewsLetterInput from "@molecules/NewsLetterInput";
import SubModal from "@molecules/SubModal";

import ChannelTalk from "@organisms/ChannelTalk";
import Footer from "@organisms/Footer";
import Header from "@organisms/Header";

import { DEFAULT_DESCRIPTION, DEFAULT_TITLE, META_TAGS } from "@const/meta";
import { URLS } from "@const/urls";

import useCheckLocation from "@hooks/useCheckLocation";
import useLanguage from "@hooks/useLanguage";

import { useLayoutStore } from "@store/layout";

ChannelService.loadScript();

export default function PageLayout() {
  const { pathname, hash } = useLocation();

  const previousLocation = useRef<string | null>(null); // 이전 경로를 저장
  const currentLocation = useRef(pathname); // 현재 경로를 저장

  const { openSubModal, closeSubModal, toast, modal, isShowFooter, meta } = useLayoutStore();
  const { lang } = useLanguage();

  const { isCheckout, isWeChat, isHome, isDanielJohnston } = useCheckLocation();

  const [showSubModal, setShowSubModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setTimeout(() => {
      setShowSubModal(true);
    }, 2000);
  }, []);

  const matchedUrlMetaTag = useMemo(() => META_TAGS[pathname as keyof typeof META_TAGS], [pathname]);

  const { data: _notice, loading: _noticeLoading } = useQuery<IMediaMetaObjectQuery>(gql(mediaMetaobjectQuery.query), {
    variables: { type: "notice", first: 2, sortKey: "updated_at" },
    onError: (error) => {
      throw new Error(error.message);
    },
  });

  const noticeList = useMemo(
    () =>
      _notice?.metaobjects?.edges.map(({ node }) => {
        const title = node?.fields.find((item) => item.key === "title");
        const content = node?.fields.find((item) => item.key === "content");
        return { handle: node.handle, title: title?.value || "", content: content?.value || "" };
      }) || [],
    [_notice],
  );

  useEffect(() => {
    if (isCheckout) {
      ChannelService.shutdown();
    }
  }, [isCheckout]);

  useEffect(() => {
    const cookie = localStorage.getItem("cookie");

    const modalList = [];

    noticeList.forEach((item) => {
      const noticeLocal = localStorage.getItem(item?.handle || "");
      if (noticeLocal) {
        // 24시간이 지난 경우
        const expireTime = JSON.parse(noticeLocal) as number;
        if (new Date().getTime() > (expireTime || 0)) {
          modalList.push({
            title: item?.title || "",
            content: item?.content,
            id: "notice",
            handle: item?.handle || "",
          });
        }
      } else {
        // 새로운 공지 일 경우
        modalList.push({
          title: item?.title || "",
          content: item?.content,
          id: "notice",
          handle: item?.handle || "",
        });
      }
    });

    if (lang === "en" && !cookie) {
      modalList.push({
        title: "Cookies Management",
        content: (
          <div className="flex flex-col gap-2.5">
            <p>
              This website uses cookie to ensure you get the best experience on our website.{" "}
              <Link as="text" href={URLS.PRIVACY_POLICY}>
                Privacy Policy
              </Link>
            </p>
            <Button
              as="square"
              onClick={() => {
                localStorage.setItem("cookie", "true");
                closeSubModal();
              }}
            >
              Agree & Close
            </Button>
          </div>
        ),
        id: "cookie",
      });
    }

    openSubModal(modalList);
  }, [_noticeLoading, lang]);

  useEffect(() => {
    if (hash.includes("newsletter")) {
      setTimeout(() => {
        openSubModal([{ title: "Subscribe to our Newsletter", content: <NewsLetterInput />, id: "newsletter" }]);
      }, 300);
    }
  }, [hash]);

  useEffect(() => {
    previousLocation.current = currentLocation.current; // 현재 경로를 이전 경로로 저장
    currentLocation.current = pathname; // 새로 업데이트된 경로를 현재 경로로 저장

    // 이전 경로에 "checkout"이 포함되면 1초간 로딩 화면을 보여줌
    if (previousLocation.current && previousLocation.current.includes("checkout")) {
      setIsLoading(true);

      // 1초 후에 로딩 상태를 false로 변경
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 1000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [location.pathname]);

  return (
    <>
      <Helmet defaultTitle={DEFAULT_TITLE}>
        <title>{matchedUrlMetaTag?.title ?? meta.title}</title>
        <meta content={matchedUrlMetaTag?.description ?? meta.description ?? DEFAULT_DESCRIPTION} name="description" />
        <meta content={matchedUrlMetaTag?.title ?? meta.title ?? DEFAULT_TITLE} property="og:title" />
        <meta
          content={matchedUrlMetaTag?.description ?? meta.description ?? DEFAULT_DESCRIPTION}
          property="og:description"
        />
        <meta content={matchedUrlMetaTag?.title ?? meta.title ?? DEFAULT_TITLE} property="twitter:title" />
        <meta
          content={matchedUrlMetaTag?.description ?? meta.description ?? DEFAULT_DESCRIPTION}
          property="twitter:description"
        />
      </Helmet>
      {!isCheckout && !isWeChat && <Header />}
      <main
        className={classNames(
          "tnt-gap-x pc:grid-cols-12 pc:px-5 relative grid min-h-dvh auto-rows-min grid-cols-8 px-4 dark:text-white",
          isDanielJohnston ? "dark:bg-dj-black" : "dark:bg-black",
          !isHome && "gap-y-2.5",
          !isCheckout && !isHome && "pc:pt-22.5 pt-13.5",
        )}
      >
        <Outlet />
        {!isCheckout && !isWeChat && lang !== "jp" ? (
          <Button className="fixed right-5 bottom-5 z-10" id="channelTalk">
            <ChannelTalk />
          </Button>
        ) : null}
      </main>
      {isShowFooter && !isCheckout && !isWeChat ? <Footer /> : null}
      {modal ? <Modal /> : null}
      {toast.length > 1 ? <Toast /> : null}
      {showSubModal ? <SubModal /> : null}

      {/*체크아웃에서 뒤로가기 할 경우, 1초동안 loading 페이지 노출 */}
      {!isCheckout && isLoading ? <Loading className="z-80" type="page" /> : null}
    </>
  );
}
